import { createSelector } from 'reselect';
import { State } from '../../reducers/State';
import {
  CommentCategoryGroup,
  CommentCategories,
  CategoryGroup,
  colorForGroup,
  CategoryOption,
  iconForGroup,
} from '../../models/CommentCategories';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { MuiTheme as theme } from 'Common/theme';
import { State as AccountState, UserStorage } from '../../reducers/Account';
import { User } from '../../models/User';
import { Organization } from '../../models/Organization';
import { HttpError } from 'sagas/Clients/HttpError';

export const accountSelector = (state: State): AccountState => state.account;

export const accountUserSelector = (state: State): User => {
  const account = accountSelector(state);
  if (!account.user) {
    throw new Error(`User is null when it should be defined`);
  }
  return account.user;
};

export const accountOrganizationsSelector = (state: State): Organization[] => {
  const account = accountSelector(state);
  return account.organizations;
};

export const userStorageSelector = (state: State): UserStorage | null => {
  const account = accountSelector(state);
  return account.userStorage;
};

export const isLoadingAccountSelector = (state: State) => {
  const account = accountSelector(state);
  return account.isLoading;
};

export const isUpdatingAccountSelector = (state: State): boolean => {
  return state.account.isUpdating;
};

export const accountErrorSelector = (
  state: State
): string | HttpError | undefined => {
  return state.account.error;
};

export const userSignupCodeSelector = (state: State): string | null => {
  return state.account.user?.developer_profile?.signup_code ?? null;
};

export const isH1AssessmentsUser = createSelector(
  userSignupCodeSelector,
  (signupCode: string | null): boolean => {
    return signupCode === 'H1ASSESSMENTS';
  }
);

export const isH1SecurityUser = createSelector(
  userSignupCodeSelector,
  (signupCode: string | null): boolean => {
    return signupCode === 'H1SECURITY';
  }
);

export const currentUserUuid = createSelector(
  accountUserSelector,
  (user: User): string => {
    return user.uuid;
  }
);

const organizationByUUID = (
  state: State,
  orgUUID: string
): Organization | undefined => {
  const organizations = accountOrganizationsSelector(state);
  return organizations.find((org: Organization) => org.uuid === orgUUID);
};

export const ownsOrganization = (state: State, orgUUID: string): boolean => {
  const orgData = organizationByUUID(state, orgUUID);
  if (!orgData) {
    return false;
  }
  return orgData.owners.some(
    (member: any) => member.uuid === currentUserUuid(state)
  );
};

export const featureFlagForOrganization = (
  state: State,
  organizationUuid: string,
  flagName: string
) => {
  const orgData = organizationByUUID(state, organizationUuid);
  return orgData?.feature_flags?.[flagName] ?? null;
};

export const featureFlagForUser = (state: State, flagName: string) => {
  // use accountSelector because we may sometimes call the before the account is loaded.
  const account = accountSelector(state);
  return account.user?.feature_flags?.[flagName] ?? null;
};

/** User Comment Category selectors */

export const commentCategoriesSelector = (
  state: State
): CommentCategories | undefined => {
  return state.account?.user?.comment_categories;
};

export const getCommentCategoriesAsOptions = (
  commentCategories?: CommentCategories
): CategoryGroup[] | undefined => {
  if (!commentCategories) {
    return undefined;
  }
  const filteredCategories = Object.keys(commentCategories).filter(
    (group: string) => {
      return commentCategories[group] !== undefined;
    }
  );
  const groups: CategoryGroup[] = filteredCategories.map((group: string) => {
    const categoryGroup: CategoryGroup = {
      label: group,
      options: getOptionsForGroup(group, commentCategories),
      color: colorForGroup[group],
      icon: iconForGroup[group],
    };
    return categoryGroup;
  });
  const otherOption: CategoryOption = {
    value: 'other',
    label: 'Other',
    color: theme.palette.grey[700],
  };
  const uncategorizedGroup: CategoryGroup = {
    label: 'uncategorized',
    options: [otherOption],
    color: theme.palette.grey[700],
    icon: faPlusCircle,
  };
  groups.push(uncategorizedGroup);
  return groups;
};

const getOptionsForGroup = (
  group: string,
  commentCategories: CommentCategories
): CategoryOption[] => {
  const categories: CommentCategoryGroup = commentCategories[group];
  return Object.keys(categories).map((category: string) => {
    const result: CategoryOption = {
      value: `${group}:${category}`,
      label: categories[category].name,
      color: colorForGroup[group],
    };
    return result;
  });
};

export const commentCategoryOptionsSelector = createSelector(
  commentCategoriesSelector,
  getCommentCategoriesAsOptions
);
